import React from 'react';
import * as Sentry from '@sentry/react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { Helmet } from 'react-helmet';
import { Trans } from 'react-i18next';
import ERROR_CODES from '@airtm/utils/dist/errorCodes/onix';
import { signupSources as SIGNUP_PLATFORM_SOURCE } from '@airtm/utils/dist/constants';
import { LoadingOverlay, KountAccessWarning } from '@airtm/airtm-ui';
import formatDate from 'utils/formatDate';
import JwtManager from 'lib/JwtManager';
import { trackCustomEvent } from 'lib/gtag4/gtag4';
import { LOCAL_STORAGE } from '@constants';
import { captchaPropsPropType, location as locationPropType } from 'utils/propTypes';
import jwtDecode from 'jwt-decode';
import { trackLoginRedirect, validateRedirectAttempts } from 'utils/monitorLoginRedirect';
import CommentsSlider from 'components/CommentsSlider/CommentsSlider';
import LoginForm from './LoginForm/LoginForm';
import MfaFormContainer from './MfaFormContainer/MfaFormContainer';
import LoginContainer from './LoginContainer';

export const LOGIN_ERRORS = {
  INVALID_CREDENTIALS: 'LOGIN:INVALID_CREDENTIALS',
  INVALID_CREDENTIALS_WITH_COUNTER: 'LOGIN:INVALID_CREDENTIALS_WITH_COUNTER',
  INVALID_2FA_CODE: 'LOGIN:INVALID_2FA_CODE',
  BLOCKED_ACCOUNT: 'LOGIN:BLOCKED_ACCOUNT',
  UNEXPECTED: 'LOGIN:UNEXPECTED_ERROR',
  EXISTING_SESSION_FOUND: 'LOGIN:EXISTING_SESSION_FOUND',
  ATTEMPTS_LIMIT_REACHED: 'LOGIN:MAX_LOGIN_ATTEMPTS_REACHED',
  INVALID_KOUNT_SESSIONID: 'LOGIN:INVALID_KOUNT_SESSIONID',
  KOUNT_CONTROL_INVALID_REQUEST: 'LOGIN:INVALID_KOUNT_ACCESS_REQUEST',
  KOUNT_LOGIN_BLOCKED: 'LOGIN:KOUNT_ACCESS_DENIED',
  KOUNT_CONTROL_SERVER_ERROR: 'LOGIN:KOUNT_ACCESS_SERVER_ERROR',
  NETWORK_CONNECTION_ERROR: 'LOGIN:NETWORK_CONNECTION_ERROR',
  INVALID_PLATFORM_SOURCE: 'LOGIN:INVALID_PLATFORM_SOURCE',
};

export class LoginComp extends React.Component {
  static propTypes = {
    location: locationPropType.isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
      replace: PropTypes.func.isRequired,
    }).isRequired,
    me: PropTypes.shape({}),
    login: PropTypes.func.isRequired,
    facebookLogin: PropTypes.func.isRequired,
    googleLogin: PropTypes.func.isRequired,
    appleLogin: PropTypes.func.isRequired,
    verifyEmailConfirm: PropTypes.func.isRequired,
    kountDeviceVerification: PropTypes.func.isRequired,
    toast: PropTypes.func.isRequired,
    toastErr: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    enableKountAccess: PropTypes.bool,
    utmData: PropTypes.shape({}).isRequired,
    captchaProps: captchaPropsPropType.isRequired,
  };

  static defaultProps = {
    enableKountAccess: true,
    me: undefined,
  };

  constructor(props) {
    super(props);

    const { state = {} } = props.location;

    this.state = {
      error: null,
      loginErrorData: null,
      submitting: false,
      loginData: {
        ...(state?.googleAuthData && { googleAuthData: state.googleAuthData }),
        ...(state?.facebookAuthData && { facebookAuthData: state.facebookAuthData }),
        ...(state?.appleAuthData && { appleAuthData: state.appleAuthData }),
      },
      mfaMethod: state?.challengeMethod || null,
      showKountInReview: false,
      kountIp: '',
    };
  }

  async componentDidMount() {
    const {
      me,
      verifyEmailConfirm,
      kountDeviceVerification,
      location,
      history,
      t,
      toast,
      toastErr,
    } = this.props;

    const { verificationEmailCode, deviceVerificationCode, ...params } = queryString.parse(
      location.search,
    );

    /**
     * This next block is now under phase out stage. It does not work as expected if user has an active session
     * /verify-email screen now is created to handled this code. This will be kept here for old links to still work.
     * Remove after a couple of weeks after this code is merged.
     */
    if (verificationEmailCode) {
      try {
        await verifyEmailConfirm({ code: verificationEmailCode });

        toast({
          title: t('LOGIN:VERIFY_EMAIL_TITLE'),
          message: t('LOGIN:VERIFY_EMAIL_MESSAGE'),
          type: 'success',
        });

        history.replace({
          search: queryString.stringify({ ...params }),
        });
      } catch (error) {
        if (error.graphQLErrors?.[0]?.extensions?.response?.status === 409) {
          toast({
            title: t('LOGIN:VERIFY_EMAIL_TITLE'),
            message: t('LOGIN:VERIFY_EMAIL_MESSAGE'),
            type: 'success',
          });
        } else {
          toastErr(error);
        }
      }
    }

    if (deviceVerificationCode) {
      try {
        await kountDeviceVerification({ code: deviceVerificationCode });

        toast({
          title: t('LOGIN:KOUNT_WHITELISTED'),
          message: t('LOGIN:KOUNT_LOGIN'),
          type: 'success',
        });

        history.replace(queryString.stringify({ ...params }));
      } catch (error) {
        toast({
          title: t('LOGIN:KOUNT_CODE_NOT_FOUND'),
          message: t('LOGIN:KOUNT_TRY_LOGIN'),
          type: 'warning',
        });
      }
    }

    const shouldHardReload = localStorage.getItem(LOCAL_STORAGE.FORCE_RELOAD) || null;

    if (shouldHardReload) {
      localStorage.removeItem(LOCAL_STORAGE.FORCE_RELOAD);
      window.location.reload(true);
    }

    if (me) {
      this.maybeRedirectTo();
    }
  }

  onLoginSuccess(token) {
    // Persist session token in manager.
    JwtManager.jwt = token.jwt;

    this.maybeRedirectTo();
  }

  onSubmitCredentials = async (values) => {
    if (!this.validateKountSessionId()) return;
    this.setState({
      error: null,
      loginErrorData: null,
      submitting: true,
      loginData: {
        credentials: values,
      },
    });
    const {
      login,
      utmData,
      captchaProps: { captchaHide, captchaMutationErrorCallback, captchaSolution },
    } = this.props;
    try {
      const {
        data: {
          login: { authenticationResponse, challengeMethod },
        },
      } = await login({
        ...values,
        captcha: captchaSolution,
        metadata: {
          ...utmData,
          oauth_source: SIGNUP_PLATFORM_SOURCE.INTERNAL,
        },
      });
      captchaHide();
      this.setState({ submitting: false });
      if (challengeMethod === null) {
        this.onLoginSuccess(authenticationResponse);
      } else {
        this.setState({ mfaMethod: challengeMethod });
      }
    } catch (error) {
      captchaMutationErrorCallback(error);
      this.setState({ submitting: false });
      this.handleLoginError(error);
    }
  };

  onFacebookLogin = async (fbResponse) => {
    if (!this.validateKountSessionId()) return;
    const { facebookLogin, utmData } = this.props;
    this.setState({
      submitting: true,
      loginData: {
        facebookAuthData: { fbResponse },
      },
    });
    try {
      const {
        data: {
          facebookLogin: { authenticationResponse, challengeMethod },
        },
      } = await facebookLogin({
        params: {
          fbResponse,
          metadata: {
            ...utmData,
            oauth_source: SIGNUP_PLATFORM_SOURCE.FACEBOOK,
          },
        },
      });

      this.setState({ submitting: false });

      if (challengeMethod === null) {
        this.onLoginSuccess(authenticationResponse);
      } else {
        this.setState({ mfaMethod: challengeMethod });
      }
    } catch (error) {
      this.setState({ submitting: false });
      this.handleLoginError(error);
    }
  };

  onGoogleLogin = async (idToken) => {
    if (!this.validateKountSessionId()) return;
    const { googleLogin, utmData } = this.props;
    this.setState({
      submitting: true,
      loginData: {
        googleAuthData: { idToken },
      },
    });
    try {
      const {
        data: {
          googleLogin: { authenticationResponse, challengeMethod },
        },
      } = await googleLogin({
        params: {
          idToken,
          metadata: {
            ...utmData,
            oauth_source: SIGNUP_PLATFORM_SOURCE.GOOGLE,
          },
        },
      });

      this.setState({ submitting: false });

      if (challengeMethod === null) {
        this.onLoginSuccess(authenticationResponse);
      } else {
        this.setState({ mfaMethod: challengeMethod });
      }
    } catch (error) {
      this.setState({ submitting: false });
      this.handleLoginError(error);
    }
  };

  onAppleLogin = async (appleAuth) => {
    if (!this.validateKountSessionId()) return;
    const { appleLogin, utmData } = this.props;
    try {
      const jwtObj = jwtDecode(appleAuth.authorization.id_token);
      const appleResponse = {
        idToken: appleAuth.authorization.id_token,
        email: appleAuth.user?.email || jwtObj.email || '',
        appleId: jwtObj.sub, // Apple user id

        user: {
          firstName: appleAuth.user?.name?.firstName || '',
          lastName: appleAuth.user?.name?.lastName || '',
        },
      };
      this.setState({
        submitting: true,
        loginData: {
          appleAuthData: { appleResponse },
        },
      });
      const {
        data: {
          appleLogin: { authenticationResponse, challengeMethod },
        },
      } = await appleLogin({
        params: {
          appleResponse,
          metadata: {
            ...utmData,
            oauth_source: SIGNUP_PLATFORM_SOURCE.APPLE,
          },
        },
      });

      this.setState({ submitting: false });

      if (challengeMethod === null) {
        this.onLoginSuccess(authenticationResponse);
      } else {
        this.setState({ mfaMethod: challengeMethod });
      }
    } catch (error) {
      this.setState({ submitting: false });
      this.handleLoginError(error);
    }
  };

  onSubmitMfa = async (mfaData) => {
    const { login, appleLogin, facebookLogin, googleLogin, utmData } = this.props;
    const {
      loginData: { credentials, appleAuthData, facebookAuthData, googleAuthData },
    } = this.state;
    this.setState({ submitting: true, error: null });

    try {
      let authenticationResponse;
      if (credentials) {
        ({
          data: {
            login: { authenticationResponse },
          },
        } = await login({
          ...credentials,
          ...mfaData,
          metadata: {
            ...utmData,
            oauth_source: SIGNUP_PLATFORM_SOURCE.INTERNAL,
          },
        }));
      } else if (appleAuthData) {
        ({
          data: {
            appleLogin: { authenticationResponse },
          },
        } = await appleLogin({
          params: {
            appleResponse: appleAuthData.appleResponse,
            metadata: {
              ...utmData,
              oauth_source: SIGNUP_PLATFORM_SOURCE.APPLE,
            },
          },
          ...mfaData,
        }));
      } else if (facebookAuthData) {
        ({
          data: {
            facebookLogin: { authenticationResponse },
          },
        } = await facebookLogin({
          params: {
            fbResponse: facebookAuthData.fbResponse,
            metadata: {
              ...utmData,
              oauth_source: SIGNUP_PLATFORM_SOURCE.FACEBOOK,
            },
          },
          ...mfaData,
        }));
      } else if (googleAuthData) {
        ({
          data: {
            googleLogin: { authenticationResponse },
          },
        } = await googleLogin({
          params: {
            idToken: googleAuthData.idToken,
            metadata: {
              ...utmData,
              oauth_source: SIGNUP_PLATFORM_SOURCE.GOOGLE,
            },
          },
          ...mfaData,
        }));
      } else {
        throw new Error('No authentication method selected');
      }

      this.setState({ submitting: false, mfaMethod: null });
      this.onLoginSuccess(authenticationResponse);
    } catch (error) {
      this.setState({ submitting: false });
      this.handleLoginError(error);
    }
  };

  validateKountSessionId = () => {
    const { enableKountAccess } = this.props;
    if (enableKountAccess && !localStorage.getItem(LOCAL_STORAGE.KOUNT_SESSIONID)) {
      this.setState({ error: LOGIN_ERRORS.INVALID_KOUNT_SESSIONID });
      return false;
    }
    return true;
  };

  handleLoginError = (error) => {
    const { t } = this.props;
    try {
      if (error.networkError) {
        if (error.networkError.statusCode === undefined) {
          this.setState({
            error: (
              <Trans
                components={[
                  <a
                    href={t('LOGIN:AIRTM_FAQ_LINK')}
                    target="_blank"
                    rel="noreferrer noopener"
                    key="faq"
                  >
                    Airtm FAQ
                  </a>,
                ]}
                i18nKey={LOGIN_ERRORS.NETWORK_CONNECTION_ERROR}
              />
            ),
          });
        } else {
          throw error.networkError;
        }
      } else if (error.graphQLErrors) {
        const [graphQLError] = error.graphQLErrors;
        const {
          extensions: { clearTime, code, errorNumber, remainingTries, response },
        } = graphQLError;

        let errorMessage;
        let loginErrorData = null;

        if (response?.status === 422) {
          const {
            body: { messages },
          } = response;
          const { history, toast } = this.props;

          if (messages.oauthSource) {
            errorMessage = LOGIN_ERRORS.INVALID_PLATFORM_SOURCE;

            this.setState({
              error: errorMessage,
              loginErrorData,
            });
          } else {
            toast({
              title: t('LOGIN:NOTIFICATION_SIGNUP_OAUTH_TITLE'),
              message: t('LOGIN:NOTIFICATION_SIGNUP_OAUTH_MESSAGE'),
              autoClose: false,
              type: 'informative',
            });
            history.replace('/sign-up');
            return;
          }
        } else if (errorNumber && !errorNumber.startsWith('222')) {
          let customEventName = '';

          switch (errorNumber) {
            case ERROR_CODES.INVALID_CREDENTIALS.code: {
              customEventName = 'INVALID_CREDENTIALS';
              if (typeof remainingTries === 'number') {
                errorMessage = LOGIN_ERRORS.INVALID_CREDENTIALS_WITH_COUNTER;
                loginErrorData = { count: remainingTries };
              } else {
                errorMessage = LOGIN_ERRORS.INVALID_CREDENTIALS;
              }
              break;
            }
            case ERROR_CODES.DELETED_ACCOUNT.code:
              customEventName = 'DELETED_ACCOUNT';
              if (typeof remainingTries === 'number') {
                errorMessage = LOGIN_ERRORS.INVALID_CREDENTIALS_WITH_COUNTER;
                loginErrorData = { count: remainingTries };
              } else {
                errorMessage = LOGIN_ERRORS.INVALID_CREDENTIALS;
              }
              break;
            case ERROR_CODES.INVALID_MFA.code:
              customEventName = 'INVALID_MFA';
              errorMessage = LOGIN_ERRORS.INVALID_2FA_CODE;
              break;
            case ERROR_CODES.BANNED_ACCOUNT.code:
              customEventName = 'BANNED_ACCOUNT';
              errorMessage = LOGIN_ERRORS.BLOCKED_ACCOUNT;
              break;
            case ERROR_CODES.UNUSABLE_ACCOUNT.code:
              customEventName = 'UNUSABLE_ACCOUNT';
              errorMessage = '';
              break;
            case ERROR_CODES.SESSION_ALREADY_ACTIVE.code:
              customEventName = 'SESSION_ALREADY_ACTIVE';
              errorMessage = LOGIN_ERRORS.EXISTING_SESSION_FOUND;
              break;
            case ERROR_CODES.KOUNT_DEVICE_NOT_TRUSTED.code:
              customEventName = 'KOUNT_DEVICE_NOT_TRUSTED';
              this.setState({
                showKountInReview: true,
              });
              return;
            default:
              customEventName = 'UNEXPECTED';
              errorMessage = LOGIN_ERRORS.UNEXPECTED;
              break;
          }

          trackCustomEvent(`error__login__${customEventName.toLowerCase()}`);

          this.setState({
            error: errorMessage,
            loginErrorData,
          });
        } else if (code) {
          trackCustomEvent(`error__login__${code.toLowerCase()}`, {});

          switch (code) {
            case 'GEETEST_CHALLENGE':
              // Don't show error
              break;
            case 'LOGIN_LIMIT_REACHED_ERROR':
              this.setState({
                error: 'LOGIN:MAX_LOGIN_ATTEMPTS_REACHED',
                loginErrorData: {
                  time: formatDate(new Date(clearTime), {
                    showTime: true,
                    showDate: false,
                    showSeconds: false,
                  }),
                },
              });
              break;
            case 'KOUNT_CONTROL_INVALID_REQUEST':
              this.setState({
                error: LOGIN_ERRORS.KOUNT_CONTROL_INVALID_REQUEST,
              });
              return;
            case 'KOUNT_LOGIN_BLOCKED':
              this.setState({
                error: LOGIN_ERRORS.KOUNT_LOGIN_BLOCKED,
              });
              return;
            case 'KOUNT_DEVICE_NOT_TRUSTED':
              this.setState({
                showKountInReview: true,
                kountIp: graphQLError.message,
              });
              return;
            case 'KOUNT_CONTROL_SERVER_ERROR':
              this.setState({
                error: LOGIN_ERRORS.KOUNT_CONTROL_SERVER_ERROR,
              });
              return;
            case 'CLOUDFRONT_504':
              this.setState({
                error: LOGIN_ERRORS.UNEXPECTED,
              });
              return;
            default:
              this.setState({
                error: code,
              });
              break;
          }
        } else {
          const {
            response: {
              body: { message },
            },
          } = error;
          throw new Error(message);
        }
      }
    } catch (unexpectedError) {
      trackCustomEvent('error__login__unhandled', {
        message: unexpectedError.message || '',
      });
      Sentry.captureException(unexpectedError);
      this.setState({ error: LOGIN_ERRORS.UNEXPECTED });
    }
  };

  maybeRedirectTo() {
    const { location, history, t, toast } = this.props;

    trackLoginRedirect();

    const destination = {
      pathname: '/',
      state: { from: '/login' },
    };

    if (location.state?.redirectPath) {
      destination.pathname = location.state.redirectPath;
    }
    if (location.state?.redirectSearch) {
      destination.search = location.state.redirectSearch;
    }
    if (location.state?.redirectHash) {
      destination.hash = location.state.redirectHash;
    }

    if (validateRedirectAttempts()) {
      history.push(destination);
    } else {
      toast({
        title: t('LOGIN:SESSION_TERMINATED_TITLE'),
        message: t('LOGIN:SESSION_TERMINATED'),
        type: 'warning',
      });
    }
  }

  render() {
    const { t, captchaProps } = this.props;
    const { submitting, error, loginData, loginErrorData, mfaMethod, showKountInReview, kountIp } =
      this.state;
    const errorToShow = typeof error === 'string' ? t(error, loginErrorData) : error;

    return (
      <>
        <Helmet>
          <title>{t('LOGIN:TITLE_LOGIN')}</title>
        </Helmet>
        <div className="row col-12">
          <div className="col-lg-6 p-0">
            <CommentsSlider />
          </div>
          {mfaMethod && !showKountInReview && (
            <div className="col-12 col-sm-10 col-md-8 col-lg-4 offset-sm-1 offset-md-2 offset-lg-1">
              <MfaFormContainer
                t={t}
                submitting={submitting}
                serverErrors={error ? { code: [t(error)] } : {}}
                onSubmit={this.onSubmitMfa}
              />
            </div>
          )}
          {!mfaMethod && !showKountInReview && (
            <div className="col-12 col-sm-10 col-md-8 col-lg-4 offset-sm-1 offset-md-2 offset-lg-1">
              <LoginForm
                captchaProps={captchaProps}
                submitting={submitting}
                initialValues={loginData}
                serverErrors={errorToShow ? { password: [errorToShow] } : {}}
                onSubmit={this.onSubmitCredentials}
                onFacebookLogin={this.onFacebookLogin}
                onGoogleLogin={this.onGoogleLogin}
                onAppleLogin={this.onAppleLogin}
              />
            </div>
          )}
          {showKountInReview && (
            <div className="col-12 col-sm-10 col-md-8 col-lg-4 offset-sm-1 offset-md-2 offset-lg-1 text--left mt-8">
              <KountAccessWarning
                kountIp={kountIp}
                textHead={t('LOGIN:KOUNT_UNKNOWN')}
                textBody={t('LOGIN:KOUNT_IP')}
                textFoot={t('LOGIN:KOUNT_CHECK_INBOX')}
              />
            </div>
          )}
        </div>

        {submitting && <LoadingOverlay />}
      </>
    );
  }
}

export default LoginContainer(LoginComp);
